import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Pill } from "@/components/ui/pill";
import { Select } from "@/components/ui/select";
import { useLines } from "@/features/lines/hooks";
import type { CalibrationPendencyKind, CalibrationPendencyStatus } from "@/types/closed-loop.types";
import type { PendencyFilterState } from "./hooks";
import { PENDENCY_KIND_LABELS, PENDENCY_STATUS_LABELS } from "./labels";

interface PendencyFiltersProps {
  value: PendencyFilterState;
  onChange: (next: PendencyFilterState) => void;
}

const KINDS: readonly CalibrationPendencyKind[] = ["MODEL_EVENT", "MEASURE_PARAM", "ENGINE_FIX"];
const STATUSES: readonly CalibrationPendencyStatus[] = [
  "OPEN",
  "RESOLVED",
  "DISCARDED",
  "SUPERSEDED",
];

// Spec 077 (FR-041) — filtros da fila de calibração, com o filtro ativo VISÍVEL.
//
// ⚠️ Filtro escondido é a forma mais barata de uma fila mentir: uma lista curta com um filtro que
// ninguém vê se lê como "quase não há trabalho". Por isso o que está ativo aparece como pílula, com
// um jeito óbvio de tirar.

export function PendencyFilters({ value, onChange }: PendencyFiltersProps) {
  const lines = useLines();
  const lineName = lines.data?.find((l) => l.id === value.lineId)?.name;
  const hasNarrowing = !!value.lineId || !!value.kind;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="pendency-status">Situação</Label>
          <Select
            id="pendency-status"
            className="w-[180px]"
            value={value.status}
            onChange={(e) =>
              onChange({ ...value, status: e.target.value as CalibrationPendencyStatus })
            }
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {PENDENCY_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pendency-line">Linha</Label>
          <Select
            id="pendency-line"
            className="w-[200px]"
            value={value.lineId ?? ""}
            onChange={(e) => onChange({ ...value, lineId: e.target.value || undefined })}
          >
            <option value="">Todas</option>
            {(lines.data ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pendency-kind">Tipo de trabalho</Label>
          <Select
            id="pendency-kind"
            className="w-[200px]"
            value={value.kind ?? ""}
            onChange={(e) =>
              onChange({ ...value, kind: (e.target.value || undefined) as CalibrationPendencyKind })
            }
          >
            <option value="">Todos</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {PENDENCY_KIND_LABELS[k]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {hasNarrowing ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-text-secondary">Filtrando por:</span>
          {value.lineId ? <Pill tone="info">{lineName ?? "linha selecionada"}</Pill> : null}
          {value.kind ? <Pill tone="info">{PENDENCY_KIND_LABELS[value.kind]}</Pill> : null}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChange({ status: value.status })}
            aria-label="Limpar filtros de linha e tipo"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Limpar
          </Button>
        </div>
      ) : null}
    </div>
  );
}
